import {
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn,
  HttpRequest
} from '@angular/common/http';
import { inject } from '@angular/core';
import dayjs from 'dayjs';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import {
  SKIP_ERROR_ALERT,
  SKIP_TOKEN_REFRESH
} from '../helpers/http-context-tokens';
import { AuthService } from './api/auth-api';
import { CommonService } from './common.service';
import { TokenRefreshService } from './token-refresh.service';

// eslint-disable-next-line rxjs-x/finnish -- names are fixed by the HttpInterceptorFn signature; they are functions, not Observables
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const tokenRefresh = inject(TokenRefreshService);
  const commonService = inject(CommonService);

  const token = auth.getToken();
  const authenticatedReq = token ? withAuthHeaders(req, token) : req;

  const retryWithFreshToken$ = (): Observable<HttpEvent<unknown>> =>
    tokenRefresh.refreshIdToken$().pipe(
      switchMap(idToken =>
        next(withBearer(authenticatedReq, idToken)).pipe(
          catchError((error: HttpErrorResponse) => {
            if (isUnauthorized(error)) {
              auth.logout();
              return throwError(() => error);
            }
            return handleError$(error, req, commonService);
          })
        )
      )
    );

  return next(authenticatedReq).pipe(
    catchError((error: HttpErrorResponse) => {
      console.error(error);
      if (
        token &&
        isUnauthorized(error) &&
        !req.context.get(SKIP_TOKEN_REFRESH)
      ) {
        return retryWithFreshToken$();
      }
      return handleError$(error, req, commonService);
    })
  );
};

function withAuthHeaders(
  req: HttpRequest<unknown>,
  token: string
): HttpRequest<unknown> {
  return req.clone({
    headers: req.headers
      .set('Authorization', `Bearer ${token}`)
      .set('pmt-utc-offset', `${dayjs().utcOffset()}`)
      .set('pmt-timezone-id', Intl.DateTimeFormat().resolvedOptions().timeZone)
  });
}

function withBearer(
  req: HttpRequest<unknown>,
  token: string
): HttpRequest<unknown> {
  return req.clone({
    headers: req.headers.set('Authorization', `Bearer ${token}`)
  });
}

function isUnauthorized(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 401;
}

function handleError$(
  error: HttpErrorResponse,
  req: HttpRequest<unknown>,
  commonService: CommonService
): Observable<never> {
  // Skip alert if request has SKIP_ERROR_ALERT context
  if (req.context.get(SKIP_ERROR_ALERT)) {
    return throwError(() => error);
  }

  return commonService.handleError$(refineErrorResponse(error));
}

function refineErrorResponse(
  errorResponse: HttpErrorResponse
): HttpErrorResponse {
  let message: string;
  switch (errorResponse.status) {
    case 400:
      message =
        'Bad Request: The server cannot process the request due to an apparent client error.';
      break;
    case 401:
      message =
        'Unauthorized: Authentication is required and has failed or has not yet been provided.';
      break;
    case 403:
      message =
        'Forbidden: Missing necessary permisions to perform the action.';
      break;
    case 404:
      message =
        'Not Found: The requested resource could not be found but may be available in the future.';
      break;
    case 405:
      message =
        'Method Not Allowed: A request method is not supported for the requested resource.';
      break;
    case 406:
      message =
        'Not Acceptable: The requested resource is capable of generating only content not acceptable according to the Accept headers sent in the request';
      break;
    case 408:
      message =
        'Request Timeout: The server timed out waiting for the request.';
      break;
    case 500:
      message = 'Internal Server Error: An unexpected error was encountered.';
      break;
    case 501:
      message =
        'Not Implemented: The server either does not recognize the request method, or it lacks the ability to fulfil the request.';
      break;
    case 502:
      message =
        'Bad Gateway: The server received an invalid response from the upstream server.';
      break;
    case 503:
      message =
        'Service Unavailable: The server temporarily cannot handle the request.';
      break;
    case 504:
      message =
        'Gateway Timeout: The server did not receive a timely response from the upstream server.';
      break;
    case 505:
      message =
        'HTTP Version Not Supported: The server does not support the HTTP version used in the request.';
      break;
    default:
      message = 'Internal Server Error: An unexpected error was encountered.';
      break;
  }

  return new HttpErrorResponse({
    error: errorResponse.error,
    headers: errorResponse.headers,
    status: errorResponse.status,
    statusText: message,
    url: errorResponse.url
  });
}
